<template lang="pug">
section.business-card-settings
    .chain-of-stores-toggle(v-if="canShowChainOfStoresToggle")
        el-form-item
            el-switch(
                v-model="hasBusinessCardPerStock"
                :active-text="$t('Chain of stores')"
            )
            .chain-of-stores-hint {{ $t('Pick which stocks each get a business card on the map.') }}

    BusinessCardsPerStock(v-if="renderPerStock" :shop-stocks="shopStocks")

    BusinessCardForm(
        v-else-if="data.businessCard"
        :value="data.businessCard"
        :cms="data"
        propPath="businessCard"
        :category-options="businessCardCategoryOptions"
        :tag-options="businessCardTagOptions"
        @remove-address="removeBusinessCardAddress"
    )
        template(#address-fallback)
            .fallback-panel
                .flex-row
                    .info.grow
                        .fallback-label {{ $t('Owner address') }}
                        .fallback-name(v-if="ownerName") {{ ownerName }}
                        .fallback-address(v-if="ownerAddressOneLiner") {{ ownerAddressOneLiner }}
                        .no-address(v-if="!ownerName && !ownerAddressOneLiner") {{ $t('No address available') }}
                    .actions
                        el-button(
                            plain
                            circle
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-pencil-alt fas"
                            @click="addBusinessCardAddress"
                        )
</template>

<i18n>
{
    "de": {
        "Owner address": "Inhaberadresse",
        "No address available": "Keine Adresse vorhanden",
        "Chain of stores": "Filialkette",
        "Pick which stocks each get a business card on the map.": "Wähle aus, welche Lager jeweils eine Visitenkarte auf der Karte erhalten."
    }
}
</i18n>

<script>
import Vue from 'vue'
import _ from 'lodash'

import { useSettingsStore } from '@/stores'

import BusinessCardForm from '@/components/businessCard/BusinessCardForm'
import BusinessCardsPerStock from '@/components/businessCard/BusinessCardsPerStock'
import StockApi from '@/extensions/shop/store/api/stock'

export default {
    name: 'AbonementBusinessCard',
    components: {
        BusinessCardForm,
        BusinessCardsPerStock,
    },
    data() {
        return {
            shopStocks: [],
        }
    },
    mounted() {
        useSettingsStore().fetchBusinessCardCategoryOptions()
        useSettingsStore().fetchBusinessCardTagOptions()
        if (this.hasShopExtension) {
            this.fetchShopStocks()
        }
        // Single-card render-target needs a non-null value to bind to —
        // BusinessCardForm mutates its `:value` prop directly.
        if (!this.renderPerStock) {
            useSettingsStore().ensureBusinessCard()
        }
    },
    watch: {
        hasShopExtension(value) {
            if (value && this.shopStocks.length === 0) {
                this.fetchShopStocks()
            }
        },
        // Toggling chain-of-stores OFF transitions us back to the single-card
        // form. If the user never had a CMS-level card before, `form.businessCard`
        // is null at this point — initialize it so the shared form has
        // something to bind to until the next save (which goes through the
        // backend's applyBusinessCardMode reconciliation).
        renderPerStock(value) {
            if (!value) {
                useSettingsStore().ensureBusinessCard()
            }
        },
    },
    computed: {
        data() {
            return useSettingsStore().form
        },
        businessCardCategoryOptions() {
            const options = useSettingsStore().options.businessCardCategory
            return (options && options.items) || []
        },
        businessCardTagOptions() {
            const options = useSettingsStore().options.businessCardTag
            return (options && options.items) || []
        },
        // Chain-of-stores: visibility gate mirrors backend
        // Cms::effective_business_card_mode (flag + shop feature + ≥2 stocks).
        // Toggle is hidden when prerequisites fail; the persisted flag stays
        // put so the setup is auto-restored when stocks come back to ≥2.
        hasShopExtension() {
            return !!_.find(_.get(this.data, 'extensions', []), function (ext) {
                return ext && ext.key === 'shop'
            })
        },
        canShowChainOfStoresToggle() {
            return this.hasShopExtension && this.shopStocks.length >= 2
        },
        renderPerStock() {
            return this.canShowChainOfStoresToggle && this.hasBusinessCardPerStock
        },
        hasBusinessCardPerStock: {
            get() {
                return !!_.get(this.data, 'has_business_card_per_stock', false)
            },
            set(value) {
                useSettingsStore().setHasBusinessCardPerStock(value)
            },
        },
        // Owner-address fallback content (shown when admin hasn't set an
        // override on the BC). Reads from the CMS owner that already comes
        // back via the CmsBase fragment — no need to consult a separate
        // account store here.
        ownerName() {
            return _.get(this.data, 'owner.user.name', '') || ''
        },
        ownerAddressOneLiner() {
            const ownerAddress = _.get(this.data, 'owner.user.address')
            if (!ownerAddress) return ''
            const parts = []
            if (ownerAddress.street) parts.push(ownerAddress.street)
            if (ownerAddress.zipcode || ownerAddress.city) {
                parts.push([ownerAddress.zipcode, ownerAddress.city].filter(Boolean).join(' '))
            }
            return parts.join(', ')
        },
    },
    methods: {
        fetchShopStocks() {
            const shopId = _.get(this.data, 'shop.id')
            if (!shopId) {
                this.shopStocks = []
                return
            }
            StockApi.fetchList(shopId)
                .then((stocks) => {
                    this.shopStocks = stocks || []
                })
                .catch(() => {
                    this.shopStocks = []
                })
        },
        addBusinessCardAddress() {
            useSettingsStore().initFormBusinessCardAddress()
        },
        removeBusinessCardAddress() {
            const store = useSettingsStore()
            store.ensureBusinessCard()
            const address = store.form.businessCard.address
            if (address && address.id) {
                Vue.set(address, 'deleted', true)
            } else {
                Vue.set(store.form.businessCard, 'address', null)
            }
            // Address validation rules are derived from the form via the
            // `rules` getter — once the address is null/deleted they drop
            // automatically.
        },
    },
}
</script>

<style lang="sass" scoped>
.business-card-settings
    .chain-of-stores-toggle
        margin-bottom: 16px
        .chain-of-stores-hint
            margin-top: 4px
            color: #909399
            font-size: 0.85em

.fallback-panel
    background: #f5f7fa
    border-radius: 4px
    padding: 12px 16px
    min-height: 36px
    line-height: 1.3
    .flex-row
        display: flex
        gap: 20px
        align-items: center
        .grow
            flex: 1
    .info
        .fallback-label
            font-size: 0.75rem
            color: #909399
            letter-spacing: 0.5px
            margin-bottom: 4px
        .fallback-name
            font-weight: 500
            color: #303133
        .fallback-address
            color: #606266
            font-size: 0.9rem
        .no-address
            color: #909399
            font-style: italic
</style>
